import React, { ReactNode, createElement } from 'react';
import { FormattedMessage, useIntl } from 'react-intl';
import parse from 'html-react-parser';

type TagReplacer = ((chunks: ReactNode) => ReactNode) | ReactNode;

interface TagReplacers {
    [key: string]: TagReplacer | string | undefined;
}

/** Flatten rich-text chunks from formatMessage into a string for the HTML-parse path (skipParsing=false). */
const stringifyRichChildren = (chunks: ReactNode): string => {
    if (chunks == null || chunks === false) {
        return '';
    }
    if (typeof chunks === 'string' || typeof chunks === 'number') {
        return String(chunks);
    }
    if (Array.isArray(chunks)) {
        return chunks.map(stringifyRichChildren).join('');
    }
    return '';
};

const defaultTagReplacers: TagReplacers = {
    strong: (chunks: ReactNode) => createElement('strong', null, chunks),
    b: (chunks: ReactNode) => createElement('b', null, chunks),
    i: (chunks: ReactNode) => createElement('i', null, chunks),
    em: (chunks: ReactNode) => createElement('em', null, chunks),
    u: (chunks: ReactNode) => createElement('u', null, chunks),
    br: createElement('br', null),
    nbsp: '\u00A0',
};

// Defaults for skipParsing=false (InstructionalText): formatMessage must resolve
// ICU rich-text tags (<strong>, <b>, …) to strings so output stays a single HTML
// string for html-react-parser. Without tag handlers, FormatJS treats `<strong>`
// etc. as rich elements; missing handlers break substitution so `{exchangeMedicaid}`
// can appear literally. `{br}` / `{nbsp}` use string literals per OR/IL bundles.
const htmlDefaultTagReplacers: TagReplacers = {
    br: '<br/>',
    nbsp: '\u00A0',
    strong: (chunks: ReactNode) => `<strong>${stringifyRichChildren(chunks)}</strong>`,
    b: (chunks: ReactNode) => `<b>${stringifyRichChildren(chunks)}</b>`,
    i: (chunks: ReactNode) => `<i>${stringifyRichChildren(chunks)}</i>`,
    em: (chunks: ReactNode) => `<em>${stringifyRichChildren(chunks)}</em>`,
    u: (chunks: ReactNode) => `<u>${stringifyRichChildren(chunks)}</u>`,
};

/**
 * A hook that provides a function to format internationalized messages with custom tag replacements.
 * It uses the `useIntl` hook from `react-intl` for message formatting and `html-react-parser`
 * to parse HTML strings into React elements. By default, it supports basic HTML tags such as
 * <strong>, <b>, <i>, <em>, and <u>, but additional custom tag replacers can be provided.
 * 
 * @returns {Function} A function `formatWithTags` that takes a message ID and an optional
 *                     object of custom tag replacements. It returns the formatted message
 *                     string with tags parsed as React elements.
 * 
 * @throws Will log a warning to the console if message formatting fails, returning the message ID.
 */
export const useIntlTagFormatter = (skipParsing = true) => {
    const intl = useIntl();

    const parseString = (message: string | ReactNode): ReactNode => {
        return typeof message === 'string' && !skipParsing ? parse(message) : message;
    };

    const intlTagFormatter = (id: string, customReplacers: TagReplacers = {}): ReactNode => {
        try {
            const enrichedReplacements: TagReplacers = {
                ...(skipParsing ? defaultTagReplacers : htmlDefaultTagReplacers),
                ...customReplacers,
            };

            const formattedMessage = intl.formatMessage({ id }, enrichedReplacements);
            return parseString(formattedMessage);
        } catch (error) {
            console.warn(`[i18n] Error formatting message with ID "${id}":`, error);
            return id;
        }
    };

    return intlTagFormatter;
};

interface ParsedFormattedMessageProps {
    id: string;
    values?: TagReplacers;
    [key: string]: any; // to allow other props like className, style, etc.
}

const ParsedFormattedMessage: React.FC<ParsedFormattedMessageProps> = ({ id, values, ...rest }) => {
    return <FormattedMessage id={id} values={{ ...defaultTagReplacers, ...values }} {...rest} />;
};

export default ParsedFormattedMessage;